import { Vue, Prop, Component, Watch } from 'vue-property-decorator'
import { fileTypeInfo, Media } from '@/modules/common/models/media.model'
import { LmsSettings } from '@/modules/learning/learning.model'
import hls from 'hls.js'
import 'media-chrome'
import { mapState } from 'vuex'

@Component({
  computed: {
    ...mapState('learning', ['lms_settings'])
  }
})

export default class GtrMediaViewer extends Vue {
  @Prop({ required: true, type: String, default: '' })
  url!: string

  @Prop({ required: false, type: Boolean, default: false })
  visible: boolean | null | undefined

  @Prop({ required: false, type: Number, default: 0 })
  mediaStartPosition: number | null | undefined

  @Prop({ required: false, type: Number, default: 0 })
  mediaRequiredPercentage: number | null | undefined

  @Prop({ required: false, type: Number, default: 0 })
  mediaPercentCompleted: number | null | undefined

  lms_settings!: LmsSettings

  mounted () {
    if (this.media?.category === 'video' || this.media?.category === 'audio') {
      const mediaPlayer: HTMLVideoElement | HTMLAudioElement | null = this.$refs.mediaPlayer as HTMLVideoElement | HTMLAudioElement
      if (mediaPlayer) {
        let mediaTimeCheckInterval

        if (this.media.category === 'video' && hls.isSupported()) {
          const hlsInstance = new hls({
            enableWebVTT: this.showSubtitlesControl
          })
          hlsInstance.loadSource(this.media.url)
          hlsInstance.attachMedia(mediaPlayer)
        } else if (mediaPlayer.canPlayType(this.media.mimeType || '')) {
          mediaPlayer.src = this.media.url
        }

        mediaPlayer.addEventListener('loadedmetadata', () => {
          mediaPlayer.currentTime = this.mediaStartPosition || 0
        })

        mediaPlayer.addEventListener('play', () => {
          mediaTimeCheckInterval = setInterval(() => {
            this.emitCurrentMediaTimeUpdate(mediaPlayer)
          }, 10000)
        })

        mediaPlayer.addEventListener('pause', () => {
          if (mediaTimeCheckInterval) {
            this.emitCurrentMediaTimeUpdate(mediaPlayer)
            clearInterval(mediaTimeCheckInterval)
          }
        })

        mediaPlayer.addEventListener('ended', () => {
          if (mediaTimeCheckInterval) {
            this.emitCurrentMediaTimeUpdate(mediaPlayer)
            clearInterval(mediaTimeCheckInterval)
          }
        })

        mediaPlayer.addEventListener('ratechange', () => {
          if (!this.showPlaybackSpeedControl) {
            mediaPlayer.playbackRate = 1
          }
        })

        mediaPlayer.addEventListener('contextmenu', (event) => {
          event.preventDefault()
        })
      }
    }
  }

  @Watch('visible')
  onVisibleChange (newVal: boolean) {
    if (!newVal) {
      const video: HTMLVideoElement | null = this.$refs.videoPlayer as HTMLVideoElement
      if (video) {
        video.pause()
      }
    }
  }

  get showSubtitlesControl (): boolean {
    return this.lms_settings?.video_player?.enable_subtitles ?? false
  }

  get showPlaybackSpeedControl (): boolean {
    return this.lms_settings?.video_player?.enable_speed_selection ?? false
  }

  get showMediaProgressBar (): boolean {
    if (this.mediaRequiredPercentage && this.mediaRequiredPercentage > 0) {
      if (this.mediaPercentCompleted) {
        return this.mediaPercentCompleted >= this.mediaRequiredPercentage
      }
    } else if (!this.mediaRequiredPercentage || this.mediaRequiredPercentage === 0) {
      return true
    }
    return false
  }

  get media (): Media | null {
    if (!this.url) {
      return null
    }

    return { url: this.url, ...fileTypeInfo(this.url) }
  }

  emitCurrentMediaTimeUpdate (video: HTMLVideoElement | HTMLAudioElement) {
    this.$emit('mediatimeupdate', video.currentTime, video.duration)
  }
}
